import React, { useMemo } from 'react';
import {
  Colors,
  Flex,
  IconHeart,
  IconHeartFilled,
  Typography,
} from '@banhala/ui-kit';
import {
  formatNumber,
  getRangeForEvent,
  isNil,
  isNotNil,
} from 'shared/src/utils';
import { useAddLikeGoods, useLikeMap } from 'shared/src/hooks';
import { useRemoveLikeGoods } from '../../../queries/useRemoveLikeGoods';
import { bridge } from 'shared/src/utils';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { usePostGoodsActionBottomSheet } from '../../PostGoodsActionBottomSheet';
import { useGoodsBasic } from '../../../queries/useGoodsBasic';
import { useLikeActionAnimation } from './LikeActionAnimation';
import { useLogGoodsAnalyticsEvent } from '../../../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsDetail } from '../../../queries/useGoodsDetail';

interface LikeButtonProps {
  initialIsLiked: boolean;
  likesCount: number;
}

export const LikeButton: React.FC<LikeButtonProps> = ({
  initialIsLiked,
  likesCount,
}) => {
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();
  const sno = useGoodsSno();
  const { likeMap, setLikeMap } = useLikeMap({
    initialLikeMap: { [sno]: initialIsLiked },
  });
  const { show: showPostGoodsActionBottomSheet } =
    usePostGoodsActionBottomSheet();

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { show: showLikeActionAnimation } = useLikeActionAnimation();

  const isLiked = likeMap[sno] ?? initialIsLiked;

  const likeCountText = useMemo(() => {
    const obj = formatNumber(likesCount);
    return `${obj.value}${obj.label}`;
  }, [likesCount]);
  const logGoodsAnaltycis = useLogGoodsAnalyticsEvent();

  const handlePress = async () => {
    if (isNil(goodsBasic) || isNil(goodsDetail)) {
      return;
    }
    if (!isLiked) {
      logGoodsAnaltycis({
        name: 'CLICK_LIKE_GOODS',
        channel: ['firebase', 'facebook', 'amplitude', 'braze', 'ablylog'],
        analytics: {
          LIKES_COUNT: goodsDetail.goods.score.likes_count,
          LIKES_COUNT_RANGE: getRangeForEvent({
            target: goodsDetail.goods.score.likes_count,
            rangeUnit: 500,
          }),
        },
      });
      // 좋아요 누르기
      const result = await addLikeGoods(sno, {
        SCREEN: 'GOODS_DETAIL',
      });
      if (isNotNil(result)) {
        await showLikeActionAnimation();
        // await sleep(10);
        await showPostGoodsActionBottomSheet({
          mode: 'like',
          title: {
            highlight: result.folderName,
            text: '에 상품을 찜했어요',
          },
          image: goodsBasic.cover_images[0],
          action: {
            text: '바로가기',
            deeplink: `ably://like/folders/${result.folderSno}`,
          },
          folderSno: result.folderSno,
        });
      }
    } else {
      logGoodsAnaltycis({
        name: 'CLICK_UNLIKE_GOODS',
        channel: ['firebase', 'facebook', 'amplitude', 'braze', 'ablylog'],
        analytics: {
          LIKES_COUNT: goodsDetail.goods.score.likes_count,
          LIKES_COUNT_RANGE: getRangeForEvent({
            target: goodsDetail.goods.score.likes_count,
            rangeUnit: 500,
          }),
        },
      });
      // 좋아요 해제하기
      await mutateRemoveLikeGoods({ sno });
      bridge.likeGoods({
        goods_sno: sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [sno]: false }));
    }
  };

  return (
    <Flex
      gap={2}
      alignItems={'center'}
      padding={{ horizontal: 12 }}
      onPress={handlePress}
      testID={`상품상세찜 selected=${isLiked}`}
    >
      {isLiked ? (
        <IconHeartFilled size={24} fill={Colors.content_pink_primary} />
      ) : (
        <IconHeart size={24} fill={Colors.content_pink_primary} />
      )}
      <Typography type={'body4'} color={'content_pink_primary'}>
        {likeCountText}
      </Typography>
    </Flex>
  );
};
